// ---------------------------------------------------------------------------
// AI-credit state: a tiny external store (same pattern as sync/status.ts) fed
// by every backend AI response, so the top bar / Settings can show remaining
// credits without polling, and the paywall dialog can open on exhaustion.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from 'react';

export interface CreditsState {
  /** Credits consumed this calendar month. Null until the first response/usage load. */
  used: number | null;
  allowance: number | null;
  exhausted: boolean;
  /** Current plan tier ('free' | 'plus' | 'pro'). Null until a backend response reports it. */
  tier: string | null;
  /**
   * Monotonic counter bumped each time the backend actually refuses an AI call
   * with 402 (see reportExhausted). The paywall dialog keys off this — not off
   * `exhausted` — so it opens on every refused attempt but never just because
   * a status fetch noticed the allowance is used up.
   */
  exhaustedEvents: number;
}

let state: CreditsState = { used: null, allowance: null, exhausted: false, tier: null, exhaustedEvents: 0 };
const listeners = new Set<() => void>();

export function getCredits(): CreditsState {
  return state;
}

export function setCredits(patch: Partial<CreditsState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

/** Record a refused (402) AI call: updates the counters and wakes the paywall. */
export function reportExhausted(patch: Partial<CreditsState> = {}): void {
  setCredits({ ...patch, exhausted: true, exhaustedEvents: state.exhaustedEvents + 1 });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useCredits(): CreditsState {
  return useSyncExternalStore(subscribe, getCredits, getCredits);
}
